///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Modeled surface wind field on the storm column floor: one quad over the
/// footprint, the Holland profile evaluated per pixel in the fragment
/// shader (storm_column_wind.glsl) with the fitted per-quadrant B.
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { type sphere } from "@stovokor/math/sphere"
import { vec2, vec4 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { type Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type StormColumnLayout } from "./stormColumnLayout"
import { type WindModel } from "./stormColumnStats"
import { type StormSstField } from "./StormSstField"

/** Lift above the floor panel so the field composites over its grid */
const cFloorLiftKm = 0.4

export class StormWindFloorNode extends NodeBase {
	private readonly floorRenderer: StormWindFloorRenderer
	private readonly startMs = performance.now()

	constructor(context: StovokorContext, layout: StormColumnLayout, model: WindModel) {
		super()
		const geometry = new StormWindFloorGeometry(context, layout)
		this.floorRenderer = new StormWindFloorRenderer(context, geometry)
		this.floorRenderer.vmaxKt.value = model.vmaxKt
		this.floorRenderer.rmwKm.value = model.rmwKm
		this.floorRenderer.hollandB.value = new vec4(model.b[0], model.b[1], model.b[2], model.b[3])
		this.floorRenderer.strike.value = layout.strike
		this.floorRenderer.center.value = new vec2(layout.centerLat, layout.centerLon)
		this.floorRenderer.sstTexture.value = context.blankTexture
		this.renderer = this.floorRenderer
		this.renderOrder = 1
	}

	public set opacity(value: number) {
		this.floorRenderer.opacity.value = value
	}

	/** Sea surface temperature under the field: faint warm/cool tint and the 26.5 °C line */
	public setSst(field: StormSstField, context: StovokorContext): void {
		this.floorRenderer.sstTexture.value = field.getTexture(context)
		this.floorRenderer.sstBounds.value = new vec4(field.bounds.minLon, field.bounds.minLat, field.bounds.maxLon, field.bounds.maxLat)
		this.floorRenderer.sstRange.value = new vec2(field.vmin, field.vmax)
		this.floorRenderer.sstEnabled.value = 1
	}

	/** Radius of the outer closed isobar, km (0 = none): a faint dashed ring */
	public setOuterClosedIsobar(rociKm: number): void {
		this.floorRenderer.rociKm.value = rociKm
	}

	/** Advance the inflow streak animation */
	public tick(): void {
		this.floorRenderer.timeSec.value = (performance.now() - this.startMs) / 1000
	}
}

class StormWindFloorRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public vmaxKt = new UniformDynamic<number>(0)
	public rmwKm = new UniformDynamic<number>(30)
	public rociKm = new UniformDynamic<number>(0)
	public hollandB = new UniformDynamic<vec4>(new vec4(1.3, 1.3, 1.3, 1.3))
	public strike = new UniformDynamic<number>(0)
	public center = new UniformDynamic<vec2>(new vec2(0, 0))
	public timeSec = new UniformDynamic<number>(0)
	public opacity = new UniformDynamic<number>(0)
	public sstTexture = new UniformDynamic<Texture>()
	public sstBounds = new UniformDynamic<vec4>(new vec4(0, 0, 1, 1))
	public sstRange = new UniformDynamic<vec2>(new vec2(-2, 35))
	public sstEnabled = new UniformDynamic<number>(0)

	constructor(context: StovokorContext, geometry: Geometry) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("storm_column_wind"), geometry, [transparentMaterial])
		this.drawState.cullFace = false
		this.drawState.depthWrite = false
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
	}
}

class StormWindFloorGeometry implements Geometry {
	public position: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true

	constructor(context: StovokorContext, layout: StormColumnLayout) {
		this.position = new VertexAttribute(context, 3)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position])
		const hx = layout.lengthKm / 2
		const hz = layout.widthKm / 2
		const y = cFloorLiftKm
		this.position.addPos(-hx, y, -hz)
		this.position.addPos(hx, y, -hz)
		this.position.addPos(hx, y, hz)
		this.position.addPos(-hx, y, hz)
		this.indexBuffer.quad(0, 1, 2, 3)
		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}
